import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

// Monumetric slot IDs from the onboarding dashboard. The div id must be
// exactly `mmt-<uuid>` — that's what their loader script looks for.
export const MMT_SLOTS = {
  /** D: 728x90/468x60/970x90, M: 320x50/320x100/300x50/300x100 */
  headerBanner: 'cea17c0b-8b41-467e-bb40-ffe2a3b0d871',
  /** D: 300x250/160x600/300x600 */
  pillarRight: 'e12b178a-5b72-4b46-9385-6b78dfc929ff',
  /** D: 300x250/160x600/300x600 */
  pillarLeft: '30f39d64-5b5c-45aa-8dc3-b316433c33c4',
  /** D: 728x90/468x60/970x90, M: 320x50/320x100/300x50/300x100 */
  bottomLeaderboard: 'e882edb2-59d8-4f50-a9bd-9788aaf0e8d9',
  /** D: 728x90/468x60, M: 320x50/320x100/300x50/300x100 */
  footerSticky: 'acf6dd59-d2b9-4c61-a12b-bd52036ee20e',
  /** D/T/M: 728x90/300x250/336x280/… */
  inContent: '0500f824-884b-4fa1-ae6c-f8cfc7944660',
} as const;

// The parts of Monumetric's runtime (monu.delivery loader) we touch.
type MmtPlacement = {
  divs?: { primary?: HTMLElement };
  disabled?: boolean;
  resetZone?: () => void;
  removeDivs?: () => void;
  stopRefresh?: () => void;
};

type MmtAdTag = {
  uuid?: string;
  config?: { uuid?: string; repeatable?: boolean };
  placements: MmtPlacement[];
  origins?: Array<{ div?: HTMLElement }>;
  done?: boolean;
};

type MmtWindow = Window & {
  $MMT?: {
    cmd?: Array<() => void>;
    display?: { slots: Array<[string]> };
    adTags?: MmtAdTag[];
    placements?: MmtPlacement[];
  };
};

function mmt() {
  return (window as MmtWindow).$MMT;
}

/**
 * Queue a slot for display, same as the inline snippet Monumetric ships. If
 * their script hasn't initialised yet the callback waits in $MMT.cmd.
 */
function queueSlot(slotId: string) {
  const w = window as MmtWindow;
  w.$MMT = w.$MMT || {};
  w.$MMT.cmd = w.$MMT.cmd || [];
  w.$MMT.cmd.push(function () {
    w.$MMT!.display!.slots.push([slotId]);
  });
}

function removeFrom<T>(list: T[] | undefined, items: T[]) {
  if (!Array.isArray(list)) return;
  for (let i = list.length - 1; i >= 0; i--) {
    if (items.includes(list[i])) list.splice(i, 1);
  }
}

/**
 * Tear down whatever Monumetric built in `node` and let the ad unit fill
 * again on its next div.
 *
 * Their ad tags mark non-repeatable units (header, pillars, footer) as done
 * after the first placement and ignore every later push, which is why only
 * the repeatable in-content unit came back after an SPA route change. This
 * mirrors their own SPA resetTags() — resetZone + removeDivs — but scoped to
 * one slot, so it works for back/forward and redirects too.
 */
function releaseSlot(slotId: string, node: HTMLElement) {
  const runtime = mmt();
  const tag = runtime?.adTags?.find((t) => (t.config?.uuid ?? t.uuid) === slotId);
  if (!tag || !Array.isArray(tag.placements)) return;

  const mine = tag.placements.filter((p) => p.divs?.primary === node);
  if (!mine.length) return;

  removeFrom(tag.placements, mine);
  removeFrom(runtime?.placements, mine);
  if (Array.isArray(tag.origins)) {
    tag.origins = tag.origins.filter((o) => o.div !== node);
  }

  for (const p of mine) {
    try {
      p.resetZone?.();
    } catch {
      /* their prebid/GPT may not be loaded yet */
    }
    try {
      if (p.divs?.primary?.parentNode) p.removeDivs?.();
      else {
        p.disabled = true;
        p.stopRefresh?.();
      }
    } catch {
      p.disabled = true;
    }
  }

  if (!tag.config?.repeatable) tag.done = false;
}

function MonumetricSlot({ slotId, className = '' }: { slotId: string; className?: string }) {
  const hostRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    // Monumetric takes over the mmt- div (renames its id, rewrites its
    // classes, removes it on reset), so it's created outside React. React
    // only owns the host wrapper.
    const node = document.createElement('div');
    node.id = `mmt-${slotId}`;
    host.appendChild(node);

    // Queue after the frame so the div is laid out before they measure it.
    const frame = window.requestAnimationFrame(() => queueSlot(slotId));

    return () => {
      window.cancelAnimationFrame(frame);
      releaseSlot(slotId, node);
      // An unclaimed div can go. A claimed one stays attached to the host
      // so their removeDivs() always has a parent to remove it from.
      if (node.parentNode === host && !node.classList.contains('mmt-primary')) {
        host.removeChild(node);
      }
    };
  }, [slotId]);

  return <div ref={hostRef} className={className} />;
}

/**
 * A Monumetric slot that is rebuilt on every client-side URL change, so each
 * route gets a fresh placement exactly like a full page load would.
 */
export function MonumetricAd({ slotId, className = '' }: { slotId: string; className?: string }) {
  const { pathname, search } = useLocation();
  return <MonumetricSlot key={`${pathname}${search}`} slotId={slotId} className={className} />;
}

/**
 * Leaderboard between the site header and the puzzle title. Breaks out of the
 * narrow game column (max-w-2xl) so 728/970-wide creatives have room, while
 * never exceeding the viewport. min-height reserves space so the ad doesn't
 * shove the title down when it fills.
 */
export function HeaderBannerAd() {
  return (
    <div className="relative left-1/2 -translate-x-1/2 w-[min(100vw,980px)] flex justify-center mb-6 [overflow-anchor:none]">
      <div className="overflow-hidden w-full min-h-[50px] max-h-[100px] md:min-h-[90px] md:max-h-[90px] flex items-center justify-center">
        <MonumetricAd
          slotId={MMT_SLOTS.headerBanner}
          className="min-h-[50px] md:min-h-[90px] flex items-center justify-center"
        />
      </div>
    </div>
  );
}

/** Track a media query without rendering hidden ad slots. */
function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(query).matches
  );

  useEffect(() => {
    const mq = window.matchMedia(query);
    const onChange = (event: MediaQueryListEvent) => setMatches(event.matches);
    setMatches(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [query]);

  return matches;
}

/**
 * Fixed pillars in the empty space either side of the game column.
 *
 * The slots are only mounted (and therefore only queued) when the viewport is
 * wide enough to actually show them. Queueing a display:none div registers a
 * zero-size slot that Monumetric never fills — that's why the pillars showed
 * nothing during QA. With a 512px game column, 1280px leaves 300px of clear
 * space on each side.
 */
export function AdPillars() {
  const wideEnough = useMediaQuery('(min-width: 1280px)');
  if (!wideEnough) return null;

  return (
    <>
      <div className="fixed left-3 top-64 z-10 w-[300px] flex justify-center">
        <MonumetricAd slotId={MMT_SLOTS.pillarLeft} />
      </div>
      <div className="fixed right-3 top-64 z-10 w-[300px] flex justify-center">
        <MonumetricAd slotId={MMT_SLOTS.pillarRight} />
      </div>
    </>
  );
}

/**
 * Footer In-screen slot. Monumetric's script renders this unit as its own
 * floating overlay with its own styling and close control, so the slot is
 * left bare — any wrapper styling of ours would just sit behind their overlay
 * as a phantom placeholder (their QA caught exactly that).
 */
export function StickyFooterAd() {
  return <MonumetricAd slotId={MMT_SLOTS.footerSticky} />;
}
